/**
 * useOutboundCall.js  ·  v3.21-1634
 *
 * Single source of truth for placing an outbound customer call from any
 * Hub surface (Client Detail, PhoneQR, DialPad, Global Call Workspace,
 * Callback controls, Call Assist, click-to-call).
 *
 * Every outbound-call surface must call `initiateOutboundCall(...)`
 * from this hook.  The hook performs the P0 client-local-time
 * preflight, renders the LateCallWarningModal when required, and
 * only then places the actual Twilio call via
 * POST /api/telephony/call.  Do NOT duplicate this logic in
 * individual components.
 */
import { useState, useCallback, useRef } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import {
  canUsePhoneSystem, canUseIntegratedPBX, PHONE_UNAUTHORIZED_MESSAGE,
} from '../lib/phone_access';
// v3.21-1754 · Canonical outbound-mode routing.  When the operator has
// explicitly opted into Browser Headset mode, the Call button places
// the call through the canonical browser Twilio Device via
// `placeBrowserCall`; otherwise the existing server-dial path runs
// unchanged.  Everything else (preflight, late-call warning, activity
// hooks) is shared between both branches.
import { useCallMode } from './useCallMode';
import { getActiveDevice, placeBrowserCall } from '../lib/browserVoice';
import { requestCallConfirmation } from '../components/ConfirmCallDialog';

export const useOutboundCall = () => {
  const { user } = useAuth();
  const uid = user?.id || user?.user_id;
  // v3.21-1754 · Read the operator's per-workstation call mode.
  // Default is STANDARD; no LocalStorage key ⇒ standard branch.
  const { isBrowserHeadset } = useCallMode(uid);
  const [calling, setCalling] = useState(false);
  const [preflight, setPreflight] = useState(null);
  // Pending call args that will be dialed after the user chooses
  // "Proceed Anyway".  Never mutate directly — swap out via setPending.
  const [pending, setPending] = useState(null);
  // Guard so a single click cannot double-fire the warning modal.
  const inFlightRef = useRef(false);

  const _placeCall = useCallback(async (args) => {
    const { phone, clientId, dealId, clientName, callAs, context } = args;
    // v3.21-1835 · explicit confirmation + server-recorded confirmation id
    inFlightRef.current = true;
    const confirmed = await requestCallConfirmation({ name: clientName, phone });
    if (!confirmed) { inFlightRef.current = false; return { ok: false, error: 'cancelled' }; }
    setCalling(true);
    let confirmationId = null;
    try {
      const { data: conf } = await api.post('/telephony/call-confirmations', {
        phone_number: phone, client_id: clientId || null, client_name: clientName || null,
        source_page: `${window.location.pathname}${context ? `#${context}` : ''}`,
        transport: isBrowserHeadset && getActiveDevice() ? 'browser_headset' : 'phone_bridge',
      });
      confirmationId = conf?.confirmation_id || null;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not confirm the call');
      setCalling(false);
      inFlightRef.current = false;
      return { ok: false, error: 'confirmation_failed' };
    }
    try {
      // v3.21-1754 · Route on the current per-workstation mode.
      // If Browser Headset is active AND the canonical Device is
      // registered in the shared singleton, place the call through
      // Device.connect(...).  Otherwise fall through to the existing
      // Standard server-dial path — unchanged from v3.21-1634.
      if (isBrowserHeadset && getActiveDevice()) {
        try {
          const call = await placeBrowserCall({
            phone,
            clientId,
            dealId,
            clientName,
            callAs: callAs || 'eqf_ptc',
            context,
            userId: user?.id || user?.user_id || null,
            companyId: user?.company_id || null,
          });
          // Bridge into the canonical Hub call state via the existing
          // custom events consumed by ActiveCallPill / Client Workspace.
          const callSid = call?.parameters?.CallSid || call?.customParameters?.get?.('CallSid') || null;
          try {
            if (clientId) {
              window.dispatchEvent(new CustomEvent('outbound-call:started', {
                detail: { clientId, callSid, clientName: clientName || null, transport: 'browser_headset' },
              }));
              window.dispatchEvent(new CustomEvent('client-workspace:open', {
                detail: { clientId, callSid },
              }));
            }
          } catch (_) { /* ignore */ }
          toast.success('Calling via Browser Headset…');
          return { ok: true, data: { call_sid: callSid, transport: 'browser_headset' } };
        } catch (e) {
          const msg = e?.message || 'Browser Headset call failed';
          toast.error(msg);
          return { ok: false, error: msg };
        } finally {
          setCalling(false);
          inFlightRef.current = false;
        }
      }
      const { data } = await api.post('/telephony/call', {
        client_number: phone,
        client_id: clientId || null,
        client_name: clientName || null,
        call_as: callAs || 'eqf_ptc',
        confirmation_id: confirmationId,
      });
      toast.success(data?.message || 'Call initiated — check your phone');
      // v3.21-1638 · Block 2 — On a successful outbound to a known
      // client, dismiss whichever launcher opened the call (DialPad /
      // PhoneQR / etc.) and open the standard Client Workspace so the
      // rep never stares at the QR after the call has started.
      if (clientId) {
        try {
          const callSid = data?.call_sid || data?.sid || data?.twilio_sid || null;
          window.dispatchEvent(new CustomEvent('outbound-call:started', {
            detail: { clientId, callSid, clientName: clientName || null },
          }));
          window.dispatchEvent(new CustomEvent('client-workspace:open', {
            detail: { clientId, callSid },
          }));
        } catch (_) { /* ignore */ }
      }
      return { ok: true, data };
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Call failed';
      toast.error(msg);
      return { ok: false, error: msg };
    } finally {
      setCalling(false);
      inFlightRef.current = false;
    }

  }, [isBrowserHeadset, user]);

  const initiateOutboundCall = useCallback(async (phone, opts = {}) => {
    // Idempotent guard — ignore multiple rapid clicks while the
    // preflight or a subsequent dial is in flight.
    if (inFlightRef.current) return;
    if (!phone) { toast.error('No phone number provided'); return; }
    if (!user) { toast.error('You must be signed in to place a call'); return; }
    if (!canUseIntegratedPBX(user)) {
      toast.error('Integrated phone system is not enabled for your company. Scan the QR code to call from your phone.');
      return;
    }
    if (!canUsePhoneSystem(user)) { toast.error(PHONE_UNAUTHORIZED_MESSAGE); return; }
    const digits = (phone || '').replace(/\D/g, '');
    if (digits.length < 10) { toast.error('Invalid phone number format'); return; }

    const {
      clientId = null,
      clientName = null,
      dealId = null,
      callAs = 'eqf_ptc',
      context = null,
    } = opts;

    inFlightRef.current = true;
    // v3.21-1634 · P0 late-call safeguard preflight.  Failure of this
    // call must NOT block the user from placing a legitimate call —
    // we degrade gracefully and dial without the warning if the
    // preflight itself fails.
    let pf = null;
    try {
      const { data } = await api.post('/telephony/outbound/preflight', {
        client_id: clientId,
        phone_number: phone,
        context,
      });
      pf = data;
    } catch (e) {
      pf = null;
    }

    if (pf && pf.warning_required) {
      setPreflight(pf);
      setPending({ phone, clientId, clientName, dealId, callAs, context });
      return;   // wait for user to Cancel / Proceed Anyway
    }

    inFlightRef.current = false;   // will be re-set inside _placeCall
    return _placeCall({ phone, clientId, clientName, dealId, callAs, context });
  }, [user, _placeCall]);

  const cancelLateCall = useCallback(() => {
    setPreflight(null);
    setPending(null);
    inFlightRef.current = false;
  }, []);

  const proceedLateCall = useCallback(async () => {
    if (!pending) { cancelLateCall(); return; }
    const pf = preflight;
    const args = pending;
    setPreflight(null);
    setPending(null);
    // Fire-and-forget audit — never blocks the call.
    try {
      api.post('/telephony/outbound/override-audit', {
        client_id: args.clientId,
        deal_id: args.dealId,
        phone_number: args.phone,
        client_local_time: pf?.client_local_time,
        timezone: pf?.timezone,
        timezone_confidence: pf?.timezone_confidence,
        warning_type: pf?.warning_type,
        context: args.context,
      });
    } catch (_) { /* noop */ }
    return _placeCall(args);
  }, [pending, preflight, cancelLateCall, _placeCall]);

  return {
    calling,
    initiateOutboundCall,
    preflight,
    proceedLateCall,
    cancelLateCall,
    lateCallWarningOpen: !!preflight,
  };
};

export default useOutboundCall;
